import {Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {RealEstate} from '../../interfaces/real-estate';
import {RealEstateService} from '../../services/real-estate/real-estate.service';
import {AsyncPipe, CurrencyPipe, DatePipe, DecimalPipe, JsonPipe, KeyValuePipe} from '@angular/common';
import {FavoritesService} from '../../services/favorites/favorites.service';
import {interval, Observable, Subject, takeUntil, takeWhile, timer} from 'rxjs';
import {initFlowbite} from 'flowbite';
import {GoogleMap, MapMarker} from '@angular/google-maps';
import {FinancialReportComponent} from '../financial-report/financial-report.component';
import {ReportService} from '../../services/report/report.service';
import {CostReport} from '../../interfaces/cost-report';
import {Base64Helper} from '../../utils/base64-helper';
import {FavoriteAlertConsentModalComponent} from '../favorite-alert-consent-modal/favorite-alert-consent-modal.component';
import {MarketingConsentService} from '../../services/marketing-consent/marketing-consent.service';

@Component({
  selector: 'app-real-estate-report',
  imports: [
    AsyncPipe,
    KeyValuePipe,
    CurrencyPipe,
    DatePipe,
    JsonPipe,
    DecimalPipe,
    GoogleMap,
    MapMarker,
    CommonModule,
    FormsModule,
    FinancialReportComponent,
    FavoriteAlertConsentModalComponent
  ],
  templateUrl: './real-estate-report.component.html',
  styleUrl: './real-estate-report.component.css'
})
export class RealEstateReportComponent implements OnInit, AfterViewInit, OnDestroy {

  realEstate: RealEstate | undefined = undefined;
  isLoading: boolean = false;
  isReportLoading: boolean = false;
  favorites: Observable<string[]>;
  mapGeoLocation: { lat: number, lng: number } | undefined = undefined;

  costReport: CostReport | undefined = undefined;
  pendingFavoriteRealEstateId: string | null = null;
  showFavoriteAlertConsentModal = false;
  isSavingFavoriteAlertConsent = false;
  favoriteAlertConsentError = '';
  private destroy$ = new Subject<void>();
  private isDestroyed = false;

  constructor(private activatedRoute: ActivatedRoute,
              private realEstateService: RealEstateService,
              private favoritesService: FavoritesService,
              private marketingConsentService: MarketingConsentService,
              private reportService: ReportService,
              private cdr: ChangeDetectorRef) {
    this.favorites = this.favoritesService.favoritesObservable;
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const id = params['id'];
      this.isLoading = true;
      this.loadRealEstate(id);
    });
  }


  private initializeReportValues() {
    console.log('initializeReportValues called');
    if (this.realEstate?.report?.financial_report) {
      console.log('Financial report found in realEstate data');
      this.costReport = { ...this.realEstate.report['financial_report'] };
      console.log('costReport assigned:', this.costReport);

      const prices = [
        this.realEstate.auction_1_price,
        this.realEstate.auction_2_price,
        this.realEstate.minimum_selling_price
      ].filter(p => p !== null && p !== undefined && p !== 0) as number[];

      const minPrice = prices.length > 0 ? Math.min(...prices) : null;

      // Fill purchase price if empty
      if (minPrice && this.costReport.purchase && !this.costReport.purchase.price_brl_cents) {
        this.costReport.purchase.price_brl_cents = minPrice * 100;
      }

      // Fill sale_projection.sale_price_gross_brl_cents with the assessment price if it's empty
      if (this.realEstate.assessment_price && this.costReport.sale_projection && !this.costReport.sale_projection.sale_price_gross_brl_cents) {
        // Convert to cents (backend seems to use cents based on naming)
        this.costReport.sale_projection.sale_price_gross_brl_cents = this.realEstate.assessment_price * 100;
        console.log('Setting sale_price_gross_brl_cents to assessment price:', this.costReport.sale_projection.sale_price_gross_brl_cents);
      }

      // Standardize brokerage to zero
      if (this.costReport.sale_projection) {
        this.costReport.sale_projection.brokerage_pct = 0;
      }
    } else {
      console.log('Financial report NOT found in realEstate data');
    }
  }

  private loadRealEstate(id: string) {
    this.realEstateService.getRealEstate(id, true).subscribe({
      next: (data: RealEstate) => {
        if (data.images && typeof data.images === 'string') {
          data.images = data.images.split(',') as string[];
        }
        this.realEstate = data;
        console.log('Real estate data loaded:', this.realEstate);
        console.log('Documents raw:', data.documents);
        console.log('Documents raw type:', typeof data.documents);
        console.log('Documents is array:', Array.isArray(data.documents));

        if (data.documents && typeof data.documents === 'object' && !Array.isArray(data.documents)) {
           // Ensure it's treated as a dictionary
           this.realEstate.documents = { ...data.documents };

           // Clean up values (e.g. remove leading/trailing whitespace which might happen in some PDFs)
           for (const key in this.realEstate.documents) {
             if (this.realEstate.documents.hasOwnProperty(key)) {
               this.realEstate.documents[key] = this.realEstate.documents[key].trim();
             }
           }
        }
        console.log('Documents processed:', this.realEstate?.documents);

        this.initializeReportValues();

        this.isLoading = false;
        this.cdr.detectChanges();

        if (this.realEstate?.report_status === ('processing' as any)) {
          this.startPolling(id, true, 30000);
        } else if (this.realEstate?.report_status !== ('completed' as any)) {
          this.generateReport();
        }

        // Initialize Flowbite after data is loaded
        this.triggerFlowbiteInit(100, () => {
          // Set default active tab
          this.switchTab('official-auction-information');
        });
      },
      error: (err) => {
        console.error('Error loading real estate details:', err);
        this.isLoading = false;
      }
    });
  }

  generateReport() {
    if (this.realEstate?.link) {
      this.isReportLoading = true;
      const id = this.realEstate.link;
      this.reportService.getReport(id).subscribe({
        next: (response) => {
          console.log('Report generation started:', response);
          this.startPolling(id, false, 5000); // Start fast polling with 5s interval
        },
        error: (err) => {
          console.error('Error starting report generation:', err);
          this.isReportLoading = false;
        }
      });
    }
  }

  private startPolling(id: string, isEncoded: boolean = false, intervalMs: number = 30000) {
    if (!isEncoded) {
      id = Base64Helper.encode(id);
    }
    this.isReportLoading = true;
    const timeout$ = timer(180000); // 3 minutes timeout
    interval(intervalMs)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(timeout$),
        takeWhile(() => this.isReportLoading)
      )
      .subscribe({
        next: () => {
          this.realEstateService.getRealEstate(id, true).subscribe({
            next: (data: RealEstate) => {
              if (data.report_status === ('completed' as any) || data.report_status === ('failed' as any)) {
                this.isReportLoading = false;
                if (data.images && typeof data.images === 'string') {
                  data.images = data.images.split(',') as string[];
                }
                this.realEstate = data;
                console.log('Real estate data updated (polling):', this.realEstate);
                if (data.documents && typeof data.documents === 'object' && !Array.isArray(data.documents)) {
                  this.realEstate.documents = { ...data.documents };
                  for (const key in this.realEstate.documents) {
                    if (this.realEstate.documents.hasOwnProperty(key)) {
                      this.realEstate.documents[key] = this.realEstate.documents[key].trim();
                    }
                  }
                }
                console.log('Documents (polling):', this.realEstate?.documents);
                this.initializeReportValues();
                this.cdr.detectChanges();
                // Initialize Flowbite after data is updated
                this.triggerFlowbiteInit(100);
              }
            },
            error: (err) => {
              console.error('Error polling for report status:', err);
              this.isReportLoading = false;
            }
          });
        },
        complete: () => {
          this.isReportLoading = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private triggerFlowbiteInit(delay: number = 100, callback?: () => void) {
    setTimeout(() => {
      if (!this.isDestroyed) {
        initFlowbite();
        if (callback) {
          callback();
        }
      }
    }, delay);
  }

  async includeFavorite(realEstateId: string): Promise<void> {
    try {
      const hasRecordedAlertDecision = sessionStorage.getItem('casato-real-estate-alerts-decision') === 'true'
        || await this.marketingConsentService.hasRecordedRealEstateAlertsDecision();

      if (hasRecordedAlertDecision) {
        this.favoritesService.includeFavorite(realEstateId);
        return;
      }
    } catch (error) {
      console.error('Failed to check favorite alert consent:', error);
    }

    this.openFavoriteAlertConsent(realEstateId);
  }

  private openFavoriteAlertConsent(realEstateId: string): void {
    this.pendingFavoriteRealEstateId = realEstateId;
    this.favoriteAlertConsentError = '';
    this.showFavoriteAlertConsentModal = true;
  }

  async saveFavoriteAlertConsent(receiveAlerts: boolean): Promise<void> {
    if (!this.pendingFavoriteRealEstateId) return;

    this.isSavingFavoriteAlertConsent = true;
    this.favoriteAlertConsentError = '';
    const realEstateId = this.pendingFavoriteRealEstateId;

    try {
      await this.marketingConsentService.saveRealEstateAlerts(receiveAlerts);
    } catch (error) {
      console.error('Failed to save favorite alert consent:', error);
    } finally {
      sessionStorage.setItem('casato-real-estate-alerts-decision', 'true');
      this.favoritesService.includeFavorite(realEstateId);
      this.closeFavoriteAlertConsent();
      this.isSavingFavoriteAlertConsent = false;
    }
  }

  private closeFavoriteAlertConsent(): void {
    this.showFavoriteAlertConsentModal = false;
    this.pendingFavoriteRealEstateId = null;
  }

  deleteFavorite(realEstateId: string) {
    this.favoritesService.deleteFavorite(realEstateId);
  }

  navigateToDetails(id: string) {
    const encodedId = Base64Helper.encode(id);
    window.open('/relatorio/' + encodedId, '_blank');
  }

  navigateToAuctioneer(url: string){
    window.open(url, '_blank')
  }

  ngAfterViewInit(): void {
    // Initialize Flowbite tabs
    this.triggerFlowbiteInit(200, () => {
      // Set default active tab
      this.switchTab('official-auction-information');
    });
  }

  // Method to manually switch tabs if needed
  switchTab(tabId: string): void {
    console.log('switchTab called with:', tabId);
    const tabsElement = document.getElementById('default-styled-tab');
    const tabContentElement = document.getElementById('default-styled-tab-content');

    if (tabsElement && tabContentElement) {
      // Hide all tab contents
      const tabContents = tabContentElement.querySelectorAll('[role="tabpanel"]');
      tabContents.forEach(content => {
        content.classList.add('hidden');
      });

      // Show the selected tab content
      const selectedContent = document.getElementById(tabId);
      if (selectedContent) {
        selectedContent.classList.remove('hidden');
      }

      // Update tab button states
      const tabButtons = tabsElement.querySelectorAll('[role="tab"]');
      tabButtons.forEach(button => {
        button.setAttribute('aria-selected', 'false');
        button.classList.remove('active');
      });

      // Set the selected tab button as active
      let buttonId = '';
      if (tabId === 'official-auction-information') buttonId = 'profile-styled-tab';
      else if (tabId === 'registration-analysis') buttonId = 'dashboard-styled-tab';
      else if (tabId === 'cost-analysis') buttonId = 'settings-styled-tab';
      else if (tabId === 'our-considerations') buttonId = 'contacts-styled-tab';
      else if (tabId === 'references') buttonId = 'references-styled-tab';

      const selectedButton = document.getElementById(buttonId);
      if (selectedButton) {
        selectedButton.setAttribute('aria-selected', 'true');
        selectedButton.classList.add('active');
      }
    }
  }

  checkPastDate(date: string | undefined): boolean {
      if (!date) return false;

      // Expecting format: %Y-%m-%dT%H:%M:%SZ (e.g., 2025-12-18T07:32:00Z)
      const isoUtcPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
      if (!isoUtcPattern.test(date)) {
        console.warn('Invalid date format (expected %Y-%m-%dT%H:%M:%SZ):', date);
        return false;
      }

      const d = new Date(date);

      if (isNaN(d.getTime())) {
        console.warn('Unable to parse date:', date);
        return false;
      }

      const now = new Date();
      return now.getTime() > d.getTime();
  }

  checkAuctionPriceBelowAssessmentPrice(): boolean{
    const now = new Date();

    if (this.realEstate?.auction_1_start_date && this.realEstate?.auction_1_price){
      let auction_date = new Date(this.realEstate?.auction_1_start_date);
      if(now.getTime() > auction_date.getTime() && this.realEstate.assessment_price > this.realEstate.auction_1_price ){
        return true;
      }
    }

    if (this.realEstate?.auction_1_start_date && this.realEstate?.auction_2_start_date && this.realEstate?.auction_2_price){
      let auction_date = new Date(this.realEstate?.auction_1_start_date);
      // return true;
      if(now.getTime() > auction_date.getTime() && this.realEstate.assessment_price > this.realEstate.auction_2_price ){
        return true;
      }
    }

    return false;
  }
}
